/**
 * PhysMath — allocation-free geometric primitives.
 *
 * Everything here works on raw numbers with module-scope scratch buffers so the
 * hot paths (BVH traversal, capsule sweeps, contact generation) never allocate.
 * These are the classic routines from Ericson, "Real-Time Collision Detection".
 *
 * owner: physics
 */

export const EPS = 1e-9;

/* ------------------------------------------------------------------ *
 * scratch — reused across calls. Never hold a reference across a call.
 * ------------------------------------------------------------------ */
export const scratchA = { x: 0, y: 0, z: 0 };
export const scratchB = { x: 0, y: 0, z: 0 };
const s0 = { x: 0, y: 0, z: 0 };
const s1 = { x: 0, y: 0, z: 0 };
const s2 = { x: 0, y: 0, z: 0 };
const s3 = { x: 0, y: 0, z: 0 };

export const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);

/**
 * Closest point on triangle (a,b,c) to point p. Writes into `out` ({x,y,z}).
 * Returns the squared distance.
 */
export function closestPointOnTriangle(
  px, py, pz,
  ax, ay, az, bx, by, bz, cx, cy, cz,
  out,
) {
  const abx = bx - ax, aby = by - ay, abz = bz - az;
  const acx = cx - ax, acy = cy - ay, acz = cz - az;
  const apx = px - ax, apy = py - ay, apz = pz - az;

  const d1 = abx * apx + aby * apy + abz * apz;
  const d2 = acx * apx + acy * apy + acz * apz;
  if (d1 <= 0 && d2 <= 0) { out.x = ax; out.y = ay; out.z = az; return sqd(px, py, pz, ax, ay, az); }

  const bpx = px - bx, bpy = py - by, bpz = pz - bz;
  const d3 = abx * bpx + aby * bpy + abz * bpz;
  const d4 = acx * bpx + acy * bpy + acz * bpz;
  if (d3 >= 0 && d4 <= d3) { out.x = bx; out.y = by; out.z = bz; return sqd(px, py, pz, bx, by, bz); }

  const vc = d1 * d4 - d3 * d2;
  if (vc <= 0 && d1 >= 0 && d3 <= 0) {
    const v = d1 / (d1 - d3 || EPS);
    out.x = ax + abx * v; out.y = ay + aby * v; out.z = az + abz * v;
    return sqd(px, py, pz, out.x, out.y, out.z);
  }

  const cpx = px - cx, cpy = py - cy, cpz = pz - cz;
  const d5 = abx * cpx + aby * cpy + abz * cpz;
  const d6 = acx * cpx + acy * cpy + acz * cpz;
  if (d6 >= 0 && d5 <= d6) { out.x = cx; out.y = cy; out.z = cz; return sqd(px, py, pz, cx, cy, cz); }

  const vb = d5 * d2 - d1 * d6;
  if (vb <= 0 && d2 >= 0 && d6 <= 0) {
    const w = d2 / (d2 - d6 || EPS);
    out.x = ax + acx * w; out.y = ay + acy * w; out.z = az + acz * w;
    return sqd(px, py, pz, out.x, out.y, out.z);
  }

  const va = d3 * d6 - d5 * d4;
  const dd1 = d4 - d3, dd2 = d5 - d6;
  if (va <= 0 && dd1 >= 0 && dd2 >= 0) {
    const w = dd1 / (dd1 + dd2 || EPS);
    out.x = bx + (cx - bx) * w; out.y = by + (cy - by) * w; out.z = bz + (cz - bz) * w;
    return sqd(px, py, pz, out.x, out.y, out.z);
  }

  const denom = 1 / (va + vb + vc || EPS);
  const v = vb * denom, w = vc * denom;
  out.x = ax + abx * v + acx * w;
  out.y = ay + aby * v + acy * w;
  out.z = az + abz * v + acz * w;
  return sqd(px, py, pz, out.x, out.y, out.z);
}

function sqd(ax, ay, az, bx, by, bz) {
  const dx = ax - bx, dy = ay - by, dz = az - bz;
  return dx * dx + dy * dy + dz * dz;
}

/**
 * Closest points between segment [p1,q1] and segment [p2,q2].
 * Writes the two closest points into out1/out2; returns squared distance.
 */
export function closestPtSegSeg(
  p1x, p1y, p1z, q1x, q1y, q1z,
  p2x, p2y, p2z, q2x, q2y, q2z,
  out1, out2,
) {
  const d1x = q1x - p1x, d1y = q1y - p1y, d1z = q1z - p1z;
  const d2x = q2x - p2x, d2y = q2y - p2y, d2z = q2z - p2z;
  const rx = p1x - p2x, ry = p1y - p2y, rz = p1z - p2z;
  const a = d1x * d1x + d1y * d1y + d1z * d1z;
  const e = d2x * d2x + d2y * d2y + d2z * d2z;
  const f = d2x * rx + d2y * ry + d2z * rz;

  let s, t;
  if (a <= EPS && e <= EPS) { s = 0; t = 0; }
  else if (a <= EPS) { s = 0; t = clamp(f / e, 0, 1); }
  else {
    const c = d1x * rx + d1y * ry + d1z * rz;
    if (e <= EPS) { t = 0; s = clamp(-c / a, 0, 1); }
    else {
      const b = d1x * d2x + d1y * d2y + d1z * d2z;
      const denom = a * e - b * b;
      s = denom !== 0 ? clamp((b * f - c * e) / denom, 0, 1) : 0;
      t = (b * s + f) / e;
      if (t < 0) { t = 0; s = clamp(-c / a, 0, 1); }
      else if (t > 1) { t = 1; s = clamp((b - c) / a, 0, 1); }
    }
  }
  out1.x = p1x + d1x * s; out1.y = p1y + d1y * s; out1.z = p1z + d1z * s;
  out2.x = p2x + d2x * t; out2.y = p2y + d2y * t; out2.z = p2z + d2z * t;
  return sqd(out1.x, out1.y, out1.z, out2.x, out2.y, out2.z);
}

/**
 * Distance between segment [p,q] and triangle (a,b,c).
 * Writes the closest point on the segment into outSeg and on the triangle into
 * outTri. Returns the (non-squared) distance. Handles the face, edge AND vertex
 * regions correctly — this is what stops the capsule catching on invisible
 * corners: we never assume the face normal.
 */
export function segTriDistance(
  px, py, pz, qx, qy, qz,
  ax, ay, az, bx, by, bz, cx, cy, cz,
  outSeg, outTri,
) {
  // Fast path: does the segment cross the triangle plane inside the triangle?
  const abx = bx - ax, aby = by - ay, abz = bz - az;
  const acx = cx - ax, acy = cy - ay, acz = cz - az;
  let nx = aby * acz - abz * acy;
  let ny = abz * acx - abx * acz;
  let nz = abx * acy - aby * acx;
  const nl = Math.sqrt(nx * nx + ny * ny + nz * nz);
  if (nl > EPS) {
    const inv = 1 / nl; nx *= inv; ny *= inv; nz *= inv;
    const dp = nx * (px - ax) + ny * (py - ay) + nz * (pz - az);
    const dq = nx * (qx - ax) + ny * (qy - ay) + nz * (qz - az);
    if ((dp < 0) !== (dq < 0)) {
      const t = dp / (dp - dq);
      const ix = px + (qx - px) * t, iy = py + (qy - py) * t, iz = pz + (qz - pz) * t;
      // point-in-triangle via closest point identity
      const d2 = closestPointOnTriangle(ix, iy, iz, ax, ay, az, bx, by, bz, cx, cy, cz, s0);
      if (d2 <= 1e-12) {
        outSeg.x = ix; outSeg.y = iy; outSeg.z = iz;
        outTri.x = ix; outTri.y = iy; outTri.z = iz;
        return 0;
      }
    }
  }

  let best = Infinity;

  // segment vs each triangle edge
  let d2 = closestPtSegSeg(px, py, pz, qx, qy, qz, ax, ay, az, bx, by, bz, s0, s1);
  if (d2 < best) { best = d2; copy(s0, outSeg); copy(s1, outTri); }
  d2 = closestPtSegSeg(px, py, pz, qx, qy, qz, bx, by, bz, cx, cy, cz, s0, s1);
  if (d2 < best) { best = d2; copy(s0, outSeg); copy(s1, outTri); }
  d2 = closestPtSegSeg(px, py, pz, qx, qy, qz, cx, cy, cz, ax, ay, az, s0, s1);
  if (d2 < best) { best = d2; copy(s0, outSeg); copy(s1, outTri); }

  // segment endpoints vs the triangle interior
  d2 = closestPointOnTriangle(px, py, pz, ax, ay, az, bx, by, bz, cx, cy, cz, s2);
  if (d2 < best) { best = d2; outSeg.x = px; outSeg.y = py; outSeg.z = pz; copy(s2, outTri); }
  d2 = closestPointOnTriangle(qx, qy, qz, ax, ay, az, bx, by, bz, cx, cy, cz, s2);
  if (d2 < best) { best = d2; outSeg.x = qx; outSeg.y = qy; outSeg.z = qz; copy(s2, outTri); }

  return Math.sqrt(best);
}

function copy(src, dst) { dst.x = src.x; dst.y = src.y; dst.z = src.z; }

/** Möller–Trumbore, two-sided. Returns t >= 0 or -1. */
export function rayTriangle(
  ox, oy, oz, dx, dy, dz,
  ax, ay, az, bx, by, bz, cx, cy, cz,
) {
  const e1x = bx - ax, e1y = by - ay, e1z = bz - az;
  const e2x = cx - ax, e2y = cy - ay, e2z = cz - az;
  const px = dy * e2z - dz * e2y;
  const py = dz * e2x - dx * e2z;
  const pz = dx * e2y - dy * e2x;
  const det = e1x * px + e1y * py + e1z * pz;
  if (det > -1e-12 && det < 1e-12) return -1;
  const invDet = 1 / det;
  const tx = ox - ax, ty = oy - ay, tz = oz - az;
  const u = (tx * px + ty * py + tz * pz) * invDet;
  if (u < -1e-7 || u > 1 + 1e-7) return -1;
  const qx = ty * e1z - tz * e1y;
  const qy = tz * e1x - tx * e1z;
  const qz = tx * e1y - ty * e1x;
  const v = (dx * qx + dy * qy + dz * qz) * invDet;
  if (v < -1e-7 || u + v > 1 + 1e-7) return -1;
  const t = (e2x * qx + e2y * qy + e2z * qz) * invDet;
  return t >= 0 ? t : -1;
}

/**
 * Conservative advancement sweep of a capsule (segment p→q, radius r) moving by
 * (dx,dy,dz) against one triangle. `maxT` is the parametric limit (usually 1).
 *
 * Writes { t, nx, ny, nz, px, py, pz, depth } into `out`. Returns true on hit.
 * `out.depth > 0` means the capsule already overlaps at t = 0.
 */
const cs0 = { x: 0, y: 0, z: 0 };
const cs1 = { x: 0, y: 0, z: 0 };
export function sweepCapsuleTriangle(
  px, py, pz, qx, qy, qz, radius,
  dx, dy, dz,
  ax, ay, az, bx, by, bz, cx, cy, cz,
  maxT, out,
) {
  let t = 0;
  const tol = 1e-4;
  for (let iter = 0; iter < 32; iter++) {
    const ox = dx * t, oy = dy * t, oz = dz * t;
    const dist = segTriDistance(
      px + ox, py + oy, pz + oz, qx + ox, qy + oy, qz + oz,
      ax, ay, az, bx, by, bz, cx, cy, cz, cs0, cs1,
    );
    const gap = dist - radius;
    if (gap <= tol) {
      out.t = t;
      out.px = cs1.x; out.py = cs1.y; out.pz = cs1.z;
      let nx = cs0.x - cs1.x, ny = cs0.y - cs1.y, nz = cs0.z - cs1.z;
      const l = Math.sqrt(nx * nx + ny * ny + nz * nz);
      if (l > 1e-7) { out.nx = nx / l; out.ny = ny / l; out.nz = nz / l; }
      else {
        // Degenerate (exactly on the surface): fall back to the face normal,
        // oriented against the motion.
        const e1x = bx - ax, e1y = by - ay, e1z = bz - az;
        const e2x = cx - ax, e2y = cy - ay, e2z = cz - az;
        let fx = e1y * e2z - e1z * e2y, fy = e1z * e2x - e1x * e2z, fz = e1x * e2y - e1y * e2x;
        const fl = Math.sqrt(fx * fx + fy * fy + fz * fz) || 1;
        fx /= fl; fy /= fl; fz /= fl;
        if (fx * dx + fy * dy + fz * dz > 0) { fx = -fx; fy = -fy; fz = -fz; }
        out.nx = fx; out.ny = fy; out.nz = fz;
      }
      out.depth = t === 0 ? Math.max(0, -gap) : 0;
      return true;
    }
    // closing speed along the separating direction
    const inv = 1 / dist;
    const sx = (cs0.x - cs1.x) * inv, sy = (cs0.y - cs1.y) * inv, sz = (cs0.z - cs1.z) * inv;
    const closing = -(dx * sx + dy * sy + dz * sz);
    if (closing <= 1e-9) return false;      // separating or parallel
    const adv = gap / closing;
    t += adv > 1e-7 ? adv : 1e-7;
    if (t > maxT) return false;
  }
  return false;
}

/** Overlap between an AABB (min/max arrays of 3) and another AABB. */
export function aabbOverlap(aMin, aMax, bMin, bMax) {
  return aMin[0] <= bMax[0] && aMax[0] >= bMin[0]
    && aMin[1] <= bMax[1] && aMax[1] >= bMin[1]
    && aMin[2] <= bMax[2] && aMax[2] >= bMin[2];
}

/** Closest point on an oriented box (centre c, rotation basis rows u0/u1/u2, half-extents e). */
export function closestPointOnOBB(px, py, pz, cx, cy, cz, m, ex, ey, ez, out) {
  // m is a column-major THREE.Matrix4 element array of the box's world matrix.
  const dx = px - cx, dy = py - cy, dz = pz - cz;
  let rx = 0, ry = 0, rz = 0;
  const e = [ex, ey, ez];
  for (let i = 0; i < 3; i++) {
    const ax = m[i * 4], ay = m[i * 4 + 1], az = m[i * 4 + 2];
    let d = dx * ax + dy * ay + dz * az;
    if (d > e[i]) d = e[i]; else if (d < -e[i]) d = -e[i];
    rx += d * ax; ry += d * ay; rz += d * az;
  }
  out.x = cx + rx; out.y = cy + ry; out.z = cz + rz;
  return out;
}
